/**
 * `seasBands` on the chart: the server's `span` shapes (`qf/geometry.py`
 * `seas_bands`) as full-height backgrounds behind the candles
 * (`docs/features/seasonality.md`). Green long, red short, **amber where a long
 * and a short overlap** (owner, 2026-10-22) — both show, neither suppresses the
 * other. Pure: shapes and bar times in, axis ranges out.
 */

import type { Plot } from "@qufolio/model";
import { indexAtTime, type TimeSpan } from "@qufolio/chart-engine";

import { axisTimeAt } from "./geometry-annotations";
import type { GeometryPayload, GeometrySpan } from "@qufolio/transport";

export const SEAS_BANDS_LAYER = "seasBands";

const COLOR = { long: "positive", short: "negative", both: "warn" } as const;
const DEFAULT_OPACITY = 0.12;

/** Whether an alias is a `seasBands` plot. */
export function isSeasBandsAlias(alias: string | undefined): boolean {
  return (alias ?? "").split("_", 1)[0] === "seasBands";
}

/**
 * Where a span's ends sit on the axis: half a bar either side of its first and
 * last bars, in **bar units** (`axisTimeAt`) — a slot edge measured in calendar
 * time lands on the wrong side of a weekend, because the axis draws every time
 * from Saturday to Monday on Monday.
 */
function placed(span: GeometrySpan, times: readonly number[]): { x1: number; x2: number; role: "long" | "short" } | null {
  if (times.length < 2) return null;
  const last = times.length - 1;
  const i1 = span.aheadFrom !== undefined ? last + span.aheadFrom : indexAtTime(times, span.from);
  const i2 = span.aheadTo !== undefined ? last + span.aheadTo : indexAtTime(times, span.to);
  if (i1 === undefined || i2 === undefined) return null;
  const x1 = axisTimeAt(times, i1 - 0.5);
  const x2 = axisTimeAt(times, i2 + 0.5);
  if (x1 === undefined || x2 === undefined || !(x2 > x1)) return null;
  return { x1, x2, role: span.role === "short" ? "short" : "long" };
}

/**
 * The backgrounds to paint, from every visible `seasBands` plot on the price pane.
 *
 * Overlaps are split, not stacked: a sweep over the ends marks each piece long,
 * short or both, so two pale boxes never darken into a third colour nobody chose.
 */
export function seasBandSpans(
  geometry: GeometryPayload | null,
  plots: readonly Plot[] | undefined,
  times: readonly number[],
): TimeSpan[] {
  if (!geometry) return [];
  const bands = (plots ?? []).filter((p) => p.type === "geometry" && !p.hidden && isSeasBandsAlias(p.outputAlias));
  if (bands.length === 0) return [];
  const opacity = bands[0]!.style?.opacity ?? DEFAULT_OPACITY;
  const ranges: { x1: number; x2: number; role: "long" | "short" }[] = [];
  for (const plot of bands) {
    for (const shape of geometry.sets[plot.outputAlias!] ?? []) {
      for (const primitive of shape.primitives) {
        if (primitive.t !== "span") continue;
        const r = placed(primitive, times);
        if (r) ranges.push(r);
      }
    }
  }
  return splitOverlaps(ranges).map((r) => ({ x1: r.x1, x2: r.x2, color: COLOR[r.kind], opacity }));
}

/** Long and short ranges into pieces that are each long, short or both. */
export function splitOverlaps(
  ranges: readonly { x1: number; x2: number; role: "long" | "short" }[],
): { x1: number; x2: number; kind: "long" | "short" | "both" }[] {
  const edges = [...new Set(ranges.flatMap((r) => [r.x1, r.x2]))].sort((a, b) => a - b);
  const out: { x1: number; x2: number; kind: "long" | "short" | "both" }[] = [];
  for (let i = 0; i + 1 < edges.length; i++) {
    const a = edges[i]!;
    const b = edges[i + 1]!;
    const mid = (a + b) / 2;
    const long = ranges.some((r) => r.role === "long" && r.x1 <= mid && mid < r.x2);
    const short = ranges.some((r) => r.role === "short" && r.x1 <= mid && mid < r.x2);
    if (!long && !short) continue;
    const kind = long && short ? "both" : long ? "long" : "short";
    const prev = out[out.length - 1];
    if (prev && prev.kind === kind && prev.x2 === a) prev.x2 = b;
    else out.push({ x1: a, x2: b, kind });
  }
  return out;
}
